import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { TDocumentDefinitions, Content } from 'pdfmake/interfaces';
import type { PdfCompanyInfo } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(pdfMake as any).vfs = (pdfFonts as any).vfs;

export interface ReceiptLine {
  description: string;
  quantity: number;
  unitPriceTtc: number;
  totalTtc: number;
}

export interface ReceiptData {
  number: string;
  docDate: string;
  docTime: string;
  clientName: string;
  lines: ReceiptLine[];
  totalHt: number;
  totalVat: number;
  totalTtc: number;
  paymentMethod: string;
  paidAmount: number;
  changeAmount: number;
}

const PAPER_WIDTH_MM = 80;
const MM_TO_PT = 2.83465;
const PAGE_WIDTH = PAPER_WIDTH_MM * MM_TO_PT;

function fmt(n: number): string {
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function fmtDate(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

export function generateReceiptPdf(
  company: PdfCompanyInfo,
  data: ReceiptData,
  action: 'print' | 'download' | 'open' = 'print',
): void {
  const headerStack: Content[] = [
    { text: company.companyName, style: 'companyName' },
    ...(company.address ? [{ text: company.address, style: 'tiny' as const }] : []),
    ...(company.phone ? [{ text: `Tél: ${company.phone}`, style: 'tiny' as const }] : []),
    ...(company.taxId ? [{ text: `MF: ${company.taxId}`, style: 'tiny' as const }] : []),
  ];

  const linesContent: Content[] = data.lines.flatMap((l) => [
    { text: l.description, style: 'lineDesc' },
    {
      columns: [
        { text: `${fmt(l.quantity)} x ${fmt(l.unitPriceTtc)}`, style: 'lineSub' },
        { text: `${fmt(l.totalTtc)}`, alignment: 'right' as const, style: 'lineTotal' },
      ],
      margin: [0, 0, 0, 4] as [number, number, number, number],
    },
  ]);

  const totalsBlock: Content = {
    stack: [
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: PAGE_WIDTH - 20, y2: 0, lineWidth: 0.5, dash: { length: 2 } }] },
      {
        columns: [
          { text: 'Total HT', style: 'totalLabel' },
          { text: `${fmt(data.totalHt)} DT`, alignment: 'right' as const, style: 'totalLabel' },
        ],
        margin: [0, 4, 0, 0] as [number, number, number, number],
      },
      {
        columns: [
          { text: 'TVA', style: 'totalLabel' },
          { text: `${fmt(data.totalVat)} DT`, alignment: 'right' as const, style: 'totalLabel' },
        ],
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: PAGE_WIDTH - 20, y2: 0, lineWidth: 1 }] },
      {
        columns: [
          { text: 'TOTAL TTC', style: 'grandTotal' },
          { text: `${fmt(data.totalTtc)} DT`, alignment: 'right' as const, style: 'grandTotal' },
        ],
        margin: [0, 4, 0, 4] as [number, number, number, number],
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: PAGE_WIDTH - 20, y2: 0, lineWidth: 0.5, dash: { length: 2 } }] },
      {
        columns: [
          { text: data.paymentMethod, style: 'totalLabel' },
          { text: `${fmt(data.paidAmount)} DT`, alignment: 'right' as const, style: 'totalLabel' },
        ],
        margin: [0, 4, 0, 0] as [number, number, number, number],
      },
      ...(data.changeAmount > 0
        ? [{
            columns: [
              { text: 'Rendu', style: 'totalLabel' as const },
              { text: `${fmt(data.changeAmount)} DT`, alignment: 'right' as const, style: 'totalLabel' as const },
            ],
          }]
        : []),
    ],
  };

  const docDef: TDocumentDefinitions = {
    pageSize: { width: PAGE_WIDTH, height: 'auto' },
    pageMargins: [10, 10, 10, 10],
    content: [
      { stack: headerStack, alignment: 'center', margin: [0, 0, 0, 6] as [number, number, number, number] },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: PAGE_WIDTH - 20, y2: 0, lineWidth: 0.5 }] },
      {
        stack: [
          { text: `Ticket ${data.number}`, style: 'ticketNumber' },
          { text: `${fmtDate(data.docDate)}  ${data.docTime}`, style: 'tiny' },
          { text: `Client: ${data.clientName}`, style: 'tiny' },
        ],
        margin: [0, 4, 0, 6] as [number, number, number, number],
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: PAGE_WIDTH - 20, y2: 0, lineWidth: 0.5 }] },
      { stack: linesContent, margin: [0, 6, 0, 6] as [number, number, number, number] },
      totalsBlock,
      { text: 'Merci de votre visite !', alignment: 'center', style: 'footer', margin: [0, 10, 0, 0] as [number, number, number, number] },
    ],
    defaultStyle: { font: 'Roboto', fontSize: 8 },
    styles: {
      companyName: { fontSize: 11, bold: true },
      tiny: { fontSize: 7 },
      ticketNumber: { fontSize: 9, bold: true },
      lineDesc: { fontSize: 8, bold: true },
      lineSub: { fontSize: 7, color: '#555' },
      lineTotal: { fontSize: 8, bold: true },
      totalLabel: { fontSize: 8 },
      grandTotal: { fontSize: 11, bold: true },
      footer: { fontSize: 8, italics: true },
    },
  };

  const pdf = pdfMake.createPdf(docDef);
  if (action === 'download') {
    pdf.download(`ticket-${data.number}.pdf`);
  } else if (action === 'open') {
    pdf.open();
  } else {
    pdf.print();
  }
}
